import {Spinner, SpinnerSize, SpinnerStyle} from '@mintdata-wsp/common/components/spinner/Spinner';
import {FrameDetailsEntry} from '@mintdata-wsp/common/state/ui/visualDesigner/SpreadsheetEditorState';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {DragGhostDirection, ResizeEvent, ResizePhase, Resizer} from '@mintdata-wsp/wsp/ui/components/resizable/Resizer';
import {MeshContextProvider} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/mesh/MeshContextProvider';
import {MeshView} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/mesh/MeshView';
import {SheetId, WorkbookId} from '@mintdata/mint-kit/api/models/Identifiers';
import {ImgIcon} from '@mintdata/mint-kit/impl/properties/components/imgIcon/ImgIcon';
import {ImgIcons} from '@mintdata/mint-kit/impl/properties/components/imgIcon/ImgIcons';
import * as classnames from 'classnames/bind';
import {action, observable} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/FrameDetailsView.less');
const cx = classnames.bind(styles);

interface IFrameDetailsViewProps
{
    workbookId:WorkbookId;
    sheetId:SheetId;
}

export interface IFrameDetailsViewState
{
    resizerInProgressOffset:number;
}

const FDC_MIN_HEIGHT_PX:number = 170;
// meshScrollSizePx + frameDetailsOffsetPx;
const FDC_BOTTOM_PX:number = 10 + 8;
// meshTableHeaderHeightPx + defaultRowHeightPx;
const FDC_TOP_PX:number = 32 + 32;

@observer
export class FrameDetailsView extends React.Component<IFrameDetailsViewProps, any>
{
    private _frameDetailsContainerRef:HTMLElement | null;

    @observable
    private _state:IFrameDetailsViewState = {
        resizerInProgressOffset: 0
    };

    public componentDidMount()
    {
        const {workbookId, sheetId} = this.props;
        const {frameDetails} = controllers.ui.spreadsheets;

        frameDetails.addFixedRowReaction(workbookId, sheetId);
    }

    public componentWillUnmount():void
    {
        const {frameDetails} = controllers.ui.spreadsheets;

        frameDetails.disposeFixedRowReaction();
    }

    private _getMaxResizerInProgressOffset():number
    {
        const {resizerInProgressOffset} = this._state;

        if( !this._frameDetailsContainerRef )
            return 0;

        const heightOfPageInPx:number = this._frameDetailsContainerRef.parentElement.getBoundingClientRect().height;
        const FDC_MAX_HEIGHT_PX:number = heightOfPageInPx - FDC_TOP_PX - FDC_BOTTOM_PX;
        const frameDetailsContainerHeightPx:number = this._frameDetailsContainerRef.getBoundingClientRect().height;

        if( resizerInProgressOffset >= 0 )
        {
            return frameDetailsContainerHeightPx - resizerInProgressOffset > FDC_MIN_HEIGHT_PX
                ? resizerInProgressOffset
                : frameDetailsContainerHeightPx - FDC_MIN_HEIGHT_PX;
        }

        return frameDetailsContainerHeightPx - resizerInProgressOffset < FDC_MAX_HEIGHT_PX
            ? resizerInProgressOffset
            : frameDetailsContainerHeightPx - FDC_MAX_HEIGHT_PX;
    }

    @action
    private _handleFrameDetailsResize = (event:ResizeEvent):void =>
    {
        if( !this._frameDetailsContainerRef )
            return;

        const {workbenchVisibility} = controllers.ui;

        const heightOfPageInPx:number = this._frameDetailsContainerRef.parentElement.getBoundingClientRect().height;
        const onePercentToPx:number = heightOfPageInPx / 100;
        const FDC_MAX_HEIGHT_PX:number = heightOfPageInPx - FDC_TOP_PX - FDC_BOTTOM_PX;

        this._state.resizerInProgressOffset += event.deltaY;

        if( event.phase === ResizePhase.Stop )
        {
            const frameDetailsContainerHeightPx:number = this._frameDetailsContainerRef.getBoundingClientRect().height;
            const maxResizerInProgressOffset:number = this._getMaxResizerInProgressOffset();
            const newHeightInPx:number = frameDetailsContainerHeightPx - maxResizerInProgressOffset;

            if( newHeightInPx < FDC_MIN_HEIGHT_PX )
            {
                workbenchVisibility.setFrameDetailsContainerHeight(FDC_MIN_HEIGHT_PX / onePercentToPx);
            }
            else if( newHeightInPx > FDC_MAX_HEIGHT_PX )
            {
                workbenchVisibility.setFrameDetailsContainerHeight(FDC_MAX_HEIGHT_PX / onePercentToPx);
            }
            else
            {
                workbenchVisibility.setFrameDetailsContainerHeight(newHeightInPx / onePercentToPx);
            }

            this._state.resizerInProgressOffset = 0;
        }
    };

    public render():JSX.Element
    {
        const {workbookId, sheetId} = this.props;
        const {workbenchVisibility, spreadsheets: {frameDetails}} = controllers.ui;
        const stack = frameDetails.getStack(workbookId, sheetId);

        if( !stack.length )
        {
            return null;
        }

        const title = frameDetails.getNestedViewTitle(workbookId, sheetId);

        const fdcHeightValuePercent:number = workbenchVisibility.getFrameDetailsContainerHeight();
        const frameDetailsContainerHeight:string = `${fdcHeightValuePercent}%`;

        return (
            <div
                className={cx('frameDetailsWrapper')}
                ref={(ref:HTMLElement | null) => this._frameDetailsContainerRef = ref}
                style={{height: frameDetailsContainerHeight}}
            >
                <Resizer
                    isVisible={true}
                    isVertical={false}
                    dragGhostDirection={DragGhostDirection.Top}
                    dragGhostOffset={this._getMaxResizerInProgressOffset()}
                    onResize={this._handleFrameDetailsResize}
                />

                <div className={cx('container')}>
                    <div className={cx('header')}>
                        <div className={cx('title')}>
                            {title}
                        </div>
                        <div className={cx('actions')}>
                            <button
                                className={cx('btn')}
                                onMouseDown={this._handleCollapseMouseDown}
                            >
                                <ImgIcon icon={ImgIcons.chevronDownWhite} className={cx('imgIcon')} />
                            </button>
                            <button
                                className={cx('btn')}
                                onMouseDown={this._handleRemoveMouseDown}
                            >
                                <ImgIcon icon={ImgIcons.close} className={cx('imgIcon')} />
                            </button>
                        </div>
                    </div>
                    <div className={cx('body')}>
                        {
                            stack.map((frameDetailsEntry) =>
                            {
                                return (
                                    <div
                                        key={frameDetailsEntry.sheet.id}
                                        className={cx('frameDetails')}
                                        style={this._getFrameDetailsStyle(frameDetailsEntry)}
                                    >
                                        <MeshContextProvider
                                            workbookId={workbookId}
                                            sheetId={frameDetailsEntry.sheet.id}
                                            isNested={true}
                                        >
                                            <MeshView
                                                isNested={true}
                                                isScrollEnabled={!frameDetails.isEntered(frameDetailsEntry)}
                                                pathInFrame={frameDetailsEntry.pathInFrame}
                                            />
                                        </MeshContextProvider>
                                    </div>
                                );
                            })
                        }
                    </div>
                </div>
            </div>
        );
    }

    private _getFrameDetailsStyle(frameDetailsEntry:FrameDetailsEntry):React.CSSProperties
    {
        const {mesh, frameDetails, workbook} = controllers.ui.spreadsheets;

        const ctx:IWSPMeshContext = workbook.getMeshContextBySheetId(frameDetailsEntry.sheet.id);
        const meshState = mesh.getState(ctx);

        if( frameDetails.isEntered(frameDetailsEntry) )
        {
            const enteredFrameCell = frameDetails.getEnteredFrameCell(frameDetailsEntry.sheet.id);
            const enteredFrameCellHeight:number = meshState.rowHeights[enteredFrameCell.rowIndex];

            return {
                flexGrow: 0,
                flexShrink: 0,
                flexBasis: `${enteredFrameCellHeight + 32}px` // 32 - column header height
            };
        }
    }

    private _handleRemoveMouseDown = (event:React.MouseEvent<HTMLElement>) =>
    {
        event.stopPropagation();

        const {workbookId, sheetId} = this.props;
        const {frameDetails, tabs} = controllers.ui.spreadsheets;

        const activeSheet = tabs.getActiveSheet(workbookId);

        frameDetails.exitNestedView(workbookId, activeSheet.id, sheetId);
    };

    private _handleCollapseMouseDown = (event:React.MouseEvent<HTMLElement>) =>
    {
        event.stopPropagation();

        if( !this._frameDetailsContainerRef )
            return;

        const {workbenchVisibility} = controllers.ui;

        const heightOfPageInPx:number = this._frameDetailsContainerRef.parentElement.getBoundingClientRect().height;
        const onePercentToPx:number = heightOfPageInPx / 100;

        workbenchVisibility.setFrameDetailsContainerHeight(FDC_MIN_HEIGHT_PX / onePercentToPx);
    };
}
